import React, { useState, useEffect, useCallback } from 'react';
import { money as moneyOf } from '../utils/money';
import './ForecastModule.css';
import { forecastYearRows } from '../utils/forecastYear';

const API = '';
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function pctOfRev(amount, total) {
  if (amount == null || !total) return '—';
  return ((amount / total) * 100).toFixed(1) + '%';
}

function formatM(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000000) return 'RM ' + (n / 1000000).toFixed(3) + 'M';
  if (abs >= 1000) return 'RM ' + Math.round(n / 1000) + 'k';
  return 'RM ' + String(Math.round(n));
}


export default function ForecastModule({ month, mode }) {
  const canEdit = mode === 'actuals';
  // Only Settings owns amount entry; report routes use the same saved tree.
  // ===== P&L entry (tree) state =====
  const [tree, setTree] = useState(null);
  const [draft, setDraft] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ===== Yearly tab state (unchanged GET /api/forecast/yearly) =====
  const [yearlyData, setYearlyData] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [yearRevOpen, setYearRevOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');

  const year = month ? month.split('-')[0] : '2026';
  const monthNum = month ? parseInt(month.split('-')[1], 10) : 5;
  const monthLabel = MONTH_NAMES[monthNum - 1] + ' ' + year;

  // ---- Load the assembled P&L tree ----
  const loadEntry = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tk = localStorage.getItem('token');
      const res = await fetch(`${API}/api/forecast?month=${month}`, {
        headers: { Authorization: `Bearer ${tk}` }
      });
      if (!res.ok) throw new Error('Failed to fetch P&L data');
      const json = await res.json();
      const sections = json.sections || [];
      setTree(sections);
      const d = {};
      for (const s of sections) for (const l of s.lines) {
        if (l.kind !== 'computed') {
          d[l.key] = { forecast: (l.kind === 'category' ? l.storedForecast : l.forecast) ?? '', actual: (l.kind === 'category' ? l.storedActual : l.actual) ?? '' };
        }
        if (l.children) for (const c of l.children) {
          d[c.key] = { forecast: c.forecast ?? '', actual: c.actual ?? '' };
        }
      }
      setDraft(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadEntry(); }, [loadEntry]);

  const setVal = (key, col, raw) =>
    setDraft(p => ({ ...p, [key]: { ...p[key], [col]: raw.replace(/[^0-9.-]/g, '') } }));

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    try {
      const tk = localStorage.getItem('token');
      const lines = Object.entries(draft).map(([line_key, v]) => ({
        line_key,
        forecast_amount: v.forecast === '' ? null : v.forecast,
        actual_amount: v.actual === '' ? null : v.actual,
      }));
      const res = await fetch(`${API}/api/forecast/values`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ month, lines }),
      });
      if (!res.ok) throw new Error('Failed to save values');
      await loadEntry();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- Yearly tab fetch ----
  const fetchYearly = useCallback(async () => {
    setYearlyLoading(true);
    try {
      const tk = localStorage.getItem('token');
      const res = await fetch(`${API}/api/forecast/yearly?year=${year}`, {
        headers: { Authorization: `Bearer ${tk}` }
      });
      if (!res.ok) throw new Error('Failed to fetch yearly data');
      const json = await res.json();
      setYearlyData(json.months || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setYearlyLoading(false);
    }
  }, [year]);

  useEffect(() => {
    if (activeTab === 'yearly') fetchYearly();
  }, [activeTab, fetchYearly, month]);

  // ========== RENDER: P&L ENTRY ==========
  const money = (v) => moneyOf(v);
  const pct = (v) => v == null ? '—' : (v * 100).toFixed(1) + '%';
  const numInput = (key, col) => (
    <input className="pnl-in" inputMode="decimal" value={draft[key]?.[col] ?? ''}
      onChange={e => setVal(key, col, e.target.value)} />
  );
  const varianceCell = (v) => {
    if (v == null) return <td className="pnl-num pnl-pct">—</td>;
    const signed = (v >= 0 ? '+' : '') + Number(v).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const color = v >= 0 ? 'var(--rmc-green-deep)' : 'var(--rmc-coral)';
    return <td className="pnl-num pnl-pct" style={{ color, fontWeight: 700 }}>{signed}</td>;
  };
  const hasActuals = tree?.some(s => s.lines.some(l => l.kind !== 'computed' && (l.actual != null || l.children?.some(c => c.actual != null))));
  const reportActual = l => l.kind === 'computed' && !hasActuals ? null : l.actual;
  const lineVariance = l => varianceCell(l.forecast == null || reportActual(l) == null ? null : l.variance);
  const editRow = (l, indent = false) => (
    <tr key={l.key} className={indent ? 'pnl-child' : ''}>
      <td className={'pnl-l' + (indent ? ' ind' : '')}>{l.label}</td>
      <td className="pnl-num">{canEdit ? numInput(l.key, 'forecast') : money(l.forecast)}</td>
      <td className="pnl-num">{canEdit ? numInput(l.key, 'actual') : money(l.actual)}</td>
      <td className="pnl-num pnl-pct">{pct(l.pctRev)}</td>
      {lineVariance(l)}
    </tr>
  );
  const computedRow = (l) => (
    <tr key={l.key} className={'pnl-calc' + (['gross_profit', 'ebitda', 'pbt', 'pat'].includes(l.key) ? ' big' : '')}>
      <td className="pnl-l">{l.label}</td>
      <td className="pnl-num pnl-ro">{money(l.forecast)}</td>
      <td className="pnl-num pnl-ro">{money(reportActual(l))}</td>
      <td className="pnl-num pnl-pct">{pct(l.pctRev)}</td>
      {lineVariance(l)}
    </tr>
  );
  const categoryRows = (l) => {
    const open = expanded.has(l.key);
    return (
      <React.Fragment key={l.key}>
        <tr className="pnl-cat">
          <td className="pnl-l">
            <span className="pnl-tog" onClick={() => setExpanded(s => { const n = new Set(s); n.has(l.key) ? n.delete(l.key) : n.add(l.key); return n; })}>
              <span className="pnl-chev">{open ? '▾' : '▸'}</span>{l.label} <span className="pnl-split">{open ? 'split' : '▸ split'}</span>
            </span>
          </td>
          <td className="pnl-num">{!canEdit || l.forecastReadonly ? money(l.forecast) : numInput(l.key, 'forecast')}</td>
          <td className="pnl-num">{!canEdit || l.actualReadonly ? money(l.actual) : numInput(l.key, 'actual')}</td>
          <td className="pnl-num pnl-pct">{pct(l.pctRev)}</td>
          {lineVariance(l)}
        </tr>
        {open && (l.children || []).map(c => editRow(c, true))}
      </React.Fragment>
    );
  };

  const renderEntry = () => {
    if (!tree) return <div className="forecast-loading">No P&amp;L data available</div>;
    return (
      <div className="forecast-card">
        <div className="forecast-card-header">
          <h3>{monthLabel} — P&amp;L Statement</h3>
          {!canEdit && <p>Read-only report. Update targets and actuals in Settings → Actuals Input. A dash means no figure has been entered.</p>}
        </div>
        {(() => {
          const lines=tree.flatMap(s=>s.lines); const find=k=>lines.find(l=>l.key===k);
          const target=find('rev_wnexus_combined')?.forecast;
          const branches=['rev_wnexus_1utama','rev_wnexus_kelana','rev_wnexus_kl'].map(k=>find(k)?.actual);
          const actual=branches.every(v=>v==null)?null:branches.reduce((s,v)=>s+(v||0),0);
          return target==null?null:<p className="forecast-combined-note"><strong>W Nexus · all branches:</strong> Sales target {money(target)} · Actual {money(actual)} · Difference {money(actual==null?null:actual-target)}. Branch targets are not allocated.</p>;
        })()}
        <div className="actuals-table-scroll" role="region" aria-label={canEdit ? "P&L data entry — scroll to view all columns" : "Target vs Actual report — scroll to view all columns"} tabIndex={0}>
        <table className="pnl-table">
          <thead>
            <tr>
              <th>Line item</th>
              <th>Target (RM)</th>
              <th>Actual (RM)</th>
              <th>% Rev</th>
              <th>Variance</th>
            </tr>
          </thead>
          <tbody>
            {tree.map(s => (
              <React.Fragment key={s.key}>
                <tr className="pnl-sec"><td colSpan={5}>{s.name}</td></tr>
                {s.lines.map(l =>
                  l.kind === 'computed' ? computedRow(l)
                    : l.kind === 'category' ? categoryRows(l)
                      : editRow(l))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
        {canEdit && <button className="pnl-save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>}
      </div>
    );
  };

  // ========== RENDER: YEARLY ==========
  const renderYearly = () => {
    if (yearlyLoading) {
      return <div className="forecast-loading">Loading yearly data...</div>;
    }
    if (!yearlyData) {
      return <div className="forecast-loading">No yearly data available</div>;
    }

    const resolvedYear = forecastYearRows(yearlyData, tree || []);
    // Build row data from the yearly months
    // Collect unique line items across all months
    const allMonthKeys = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);

    // Get line items from first available month
    const firstMonth = allMonthKeys.find(k => resolvedYear[k] && resolvedYear[k].length > 0);
    if (!firstMonth) return <div className="forecast-loading">No forecast data for {year}</div>;

    const templateLines = resolvedYear[firstMonth].sort((a, b) => a.sort_order - b.sort_order);

    // Group by section
    const sections = [
      { key: 'revenue', label: 'Revenue (Target)', lines: templateLines.filter(l => l.section === 'revenue') },
      { key: 'cogs', label: 'COGS', lines: templateLines.filter(l => l.section === 'cogs') },
      { key: 'profit_sharing', label: 'Profit Sharing', lines: templateLines.filter(l => l.section === 'profit_sharing') },
      { key: 'opex', label: 'OPEX', lines: templateLines.filter(l => l.section === 'opex') },
      { key: 'below_ebitda', label: 'Below EBITDA', lines: templateLines.filter(l => l.section === 'below_ebitda') },
    ];

    const getMonthVal = (mKey, lineKey, field) => {
      const mLines = resolvedYear[mKey] || [];
      const line = mLines.find(l => l.line_key === lineKey);
      return line ? parseFloat(line[field]) || 0 : 0;
    };

    const sumSectionMonth = (mKey, section, field) => {
      const mLines = (resolvedYear[mKey] || []).filter(l => l.section === section);
      return mLines.reduce((s, l) => s + (parseFloat(l[field]) || 0), 0);
    };

    // Helper: get actual for a month, returns null if no actual_amount set
    const getMonthActual = (mKey, lineKey) => {
      const mLines = resolvedYear[mKey] || [];
      const line = mLines.find(l => l.line_key === lineKey);
      return line && line.actual_amount != null ? parseFloat(line.actual_amount) || 0 : null;
    };

    const sumSectionMonthActual = (mKey, section) => {
      const mLines = (resolvedYear[mKey] || []).filter(l => l.section === section);
      const withActual = mLines.filter(l => l.actual_amount != null);
      if (withActual.length === 0) return null;
      return withActual.reduce((s, l) => s + (parseFloat(l.actual_amount) || 0), 0);
    };

    // Compute forecast totals
    const monthRevF = allMonthKeys.map(k => sumSectionMonth(k, 'revenue', 'forecast_amount'));
    const monthRevA = allMonthKeys.map(k => sumSectionMonthActual(k, 'revenue'));
    const monthCogsF = allMonthKeys.map(k => sumSectionMonth(k, 'cogs', 'forecast_amount'));
    const monthCogsA = allMonthKeys.map(k => sumSectionMonthActual(k, 'cogs'));
    const monthGPF = monthRevF.map((r, i) => r - monthCogsF[i]);
    const monthGPA = monthRevA.map((r, i) => r != null && monthCogsA[i] != null ? r - monthCogsA[i] : null);
    const monthPSF = allMonthKeys.map(k => sumSectionMonth(k, 'profit_sharing', 'forecast_amount'));
    const monthPSA = allMonthKeys.map(k => sumSectionMonthActual(k, 'profit_sharing'));
    const monthOpexF = allMonthKeys.map(k => sumSectionMonth(k, 'opex', 'forecast_amount'));
    const monthOpexA = allMonthKeys.map(k => sumSectionMonthActual(k, 'opex'));
    const monthDedF = monthPSF.map((ps, i) => ps + monthOpexF[i]);
    const monthDedA = monthPSA.map((ps, i) => ps != null && monthOpexA[i] != null ? ps + monthOpexA[i] : null);
    const monthEbitdaF = monthGPF.map((gp, i) => gp - monthDedF[i]);
    const monthEbitdaA = monthGPA.map((gp, i) => gp != null && monthDedA[i] != null ? gp - monthDedA[i] : null);
    const monthBelowF = allMonthKeys.map(k => sumSectionMonth(k, 'below_ebitda', 'forecast_amount'));
    const monthBelowA = allMonthKeys.map(k => sumSectionMonthActual(k, 'below_ebitda'));
    const monthPbtF = monthEbitdaF.map((e, i) => e - monthBelowF[i]);
    const monthPbtA = monthEbitdaA.map((e, i) => e != null && monthBelowA[i] != null ? e - monthBelowA[i] : null);

    const yearTotal = (arr) => arr.reduce((s, v) => s + (v || 0), 0);
    const yearTotalNullable = (arr) => {
      const valid = arr.filter(v => v != null);
      return valid.length > 0 ? valid.reduce((s, v) => s + v, 0) : null;
    };
    const totalRevYear = yearTotal(monthRevF);

    // Render helpers
    const renderRow = (label, values, rowClass, style, totalOverride, pctRev) => (
      <tr className={rowClass || ''} key={label}>
        <td>{rowClass ? <strong style={style}>{label}</strong> : <span style={style}>{label}</span>}</td>
        {values.map((v, i) => {
          const isCurrent = (i + 1) === monthNum;
          const isFuture = (i + 1) > monthNum;
          return (
            <td key={i} className={isFuture && v != null ? 'future' : ''} style={isCurrent ? { color: style?.color || 'var(--rmc-amber)' } : (style || {})}>
              {v != null ? formatM(v) : <span className="dim">—</span>}
            </td>
          );
        })}
        <td className="total-col" style={style}>{formatM(totalOverride != null ? totalOverride : yearTotal(values))}</td>
        <td className="pct-col">{pctRev != null ? pctRev : pctOfRev(yearTotal(values), totalRevYear)}</td>
      </tr>
    );

    const renderVarianceRow = (label, forecast, actual) => (
      <tr className="variance-row" key={label + '-var'}>
        <td><span style={{ color: 'var(--rmc-ink3)', fontStyle: 'italic', fontSize: 11 }}>{label}</span></td>
        {forecast.map((f, i) => {
          const a = actual[i];
          if (a == null || !f) return <td key={i} className="dim">—</td>;
          const p = ((a - f) / Math.abs(f) * 100).toFixed(1);
          const isGood = label.includes('Revenue') || label.includes('GP') || label.includes('EBITDA') || label.includes('PBT')
            ? parseFloat(p) >= 0 : parseFloat(p) <= 0;
          return <td key={i} className={isGood ? 'pos' : 'neg'} style={{ fontSize: 11 }}>{p > 0 ? '+' : ''}{p}%</td>;
        })}
        <td className="dim" style={{ fontSize: 11 }}>
          {yearTotalNullable(actual) != null ? (() => {
            const tA = yearTotalNullable(actual);
            const tF = yearTotal(forecast);
            const p = ((tA - tF) / Math.abs(tF) * 100).toFixed(1);
            return `${p > 0 ? '+' : ''}${p}%`;
          })() : '—'}
        </td>
        <td></td>
      </tr>
    );

    const revLines = sections[0].lines;

    // Target = muted (secondary); Actual = bold ink (primary); key metrics tinted.
    const TGT = { color: 'var(--rmc-ink3)' };
    const ACT = { color: 'var(--rmc-ink)', fontWeight: 600 };
    const ACT_GP = { color: 'var(--rmc-green)', fontWeight: 600 };

    return (
      <div className="forecast-card">
        <div className="forecast-card-header">
          <h3>{year} Full Year P&amp;L — Target vs Actual</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="forecast-yearly-table">
            <thead>
              <tr>
                <th style={{ minWidth: 180 }}>Line Item</th>
                {MONTH_NAMES.map((m, i) => (
                  <th key={m} className={(i + 1) === monthNum ? 'current' : ''}>{m}</th>
                ))}
                <th>Total</th>
                <th>%Rev</th>
              </tr>
            </thead>
            <tbody>
              {/* ===== REVENUE ===== */}
              <tr className="section-row"><td colSpan="15">Revenue</td></tr>
              {yearRevOpen && revLines.map(l => {
                const fVals = allMonthKeys.map(k => getMonthVal(k, l.line_key, 'forecast_amount'));
                const aVals = allMonthKeys.map(k => getMonthActual(k, l.line_key));
                const hasAny = aVals.some(v => v != null);
                return (
                  <React.Fragment key={l.line_key}>
                    {renderRow(l.line_label + ' · Target', fVals, '', TGT, null, null)}
                    {hasAny && renderRow(l.line_label + ' · Actual', aVals, '', ACT, null, null)}
                  </React.Fragment>
                );
              })}
              <tr className="ytoggle-row" onClick={() => setYearRevOpen(o => !o)}>
                <td colSpan="15">{yearRevOpen ? '– hide revenue lines' : `+ show ${revLines.length} revenue lines`}</td>
              </tr>
              {renderRow('Total Revenue · Target', monthRevF, 'subtotal-row', TGT, null, '100%')}
              {monthRevA.some(v => v != null) && renderRow('Total Revenue · Actual', monthRevA, 'subtotal-row', ACT, null, null)}
              {monthRevA.some(v => v != null) && renderVarianceRow('Revenue Variance', monthRevF, monthRevA)}

              {/* ===== COGS ===== */}
              <tr className="section-row"><td colSpan="15">COGS</td></tr>
              {renderRow('Total COGS · Target', monthCogsF, 'subtotal-row', TGT, null, null)}
              {monthCogsA.some(v => v != null) && renderRow('Total COGS · Actual', monthCogsA, 'subtotal-row', ACT, null, null)}
              {monthCogsA.some(v => v != null) && renderVarianceRow('COGS Variance', monthCogsF, monthCogsA)}

              {/* ===== GROSS PROFIT ===== */}
              {renderRow('Gross Profit · Target', monthGPF, 'subtotal-row', TGT, null, null)}
              {monthGPA.some(v => v != null) && renderRow('Gross Profit · Actual', monthGPA, 'subtotal-row', ACT_GP, null, null)}
              {monthGPA.some(v => v != null) && renderVarianceRow('GP Variance', monthGPF, monthGPA)}

              {/* ===== DEDUCTIONS ===== */}
              <tr className="section-row"><td colSpan="15">Profit Sharing + OPEX</td></tr>
              {renderRow('Total Deductions · Target', monthDedF, 'subtotal-row', TGT, null, null)}
              {monthDedA.some(v => v != null) && renderRow('Total Deductions · Actual', monthDedA, 'subtotal-row', ACT, null, null)}

              {/* ===== EBITDA ===== */}
              <tr className="ebitda-row">
                <td><strong style={TGT}>EBITDA · Target</strong></td>
                {monthEbitdaF.map((v, i) => (
                  <td key={i} className={(i + 1) > monthNum ? 'future' : ''} style={TGT}>{formatM(v)}</td>
                ))}
                <td className="total-col" style={TGT}>{formatM(yearTotal(monthEbitdaF))}</td>
                <td className="pct-col">{pctOfRev(yearTotal(monthEbitdaF), totalRevYear)}</td>
              </tr>
              {monthEbitdaA.some(v => v != null) && (
                <tr className="ebitda-row" style={{ borderTop: 'none' }}>
                  <td><strong>EBITDA · Actual</strong></td>
                  {monthEbitdaA.map((v, i) => (
                    <td key={i}>{v != null ? formatM(v) : <span className="dim">—</span>}</td>
                  ))}
                  <td className="total-col">{formatM(yearTotalNullable(monthEbitdaA))}</td>
                  <td className="pct-col"></td>
                </tr>
              )}
              {monthEbitdaA.some(v => v != null) && renderVarianceRow('EBITDA Variance', monthEbitdaF, monthEbitdaA)}

              {/* ===== PBT ===== */}
              <tr className="pbt-row">
                <td style={TGT}>PBT · Target</td>
                {monthPbtF.map((v, i) => (
                  <td key={i} className={(i + 1) > monthNum ? 'future' : ''} style={TGT}>{formatM(v)}</td>
                ))}
                <td className="total-col" style={TGT}>{formatM(yearTotal(monthPbtF))}</td>
                <td className="pct-col">{pctOfRev(yearTotal(monthPbtF), totalRevYear)}</td>
              </tr>
              {monthPbtA.some(v => v != null) && (
                <tr className="pbt-row" style={{ borderTop: 'none' }}>
                  <td>PBT · Actual</td>
                  {monthPbtA.map((v, i) => (
                    <td key={i}>{v != null ? formatM(v) : <span className="dim">—</span>}</td>
                  ))}
                  <td className="total-col">{formatM(yearTotalNullable(monthPbtA))}</td>
                  <td className="pct-col"></td>
                </tr>
              )}
              {monthPbtA.some(v => v != null) && renderVarianceRow('PBT Variance', monthPbtF, monthPbtA)}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ========== MAIN RENDER ==========
  if (loading) {
    return <div className="forecast-loading">Loading P&amp;L data...</div>;
  }

  // Actuals Input lives on its own page (Settings → Actuals Input) — admin enters
  // the close numbers there. Render just the P&L entry, no tabs.
  if (mode === 'actuals') {
    return (
      <div className="forecast-page">
        {error && <div className="forecast-error">{error}</div>}
        {renderEntry()}
      </div>
    );
  }

  return (
    <div className="forecast-page">
      {error && (
        <div className="forecast-error">{error}</div>
      )}

      <div className="forecast-tab-bar">
        <button
          className={`forecast-tab-item ${activeTab === 'entry' ? 'active' : ''}`}
          onClick={() => setActiveTab('entry')}
        >
          P&amp;L Statement
        </button>
        <button
          className={`forecast-tab-item ${activeTab === 'yearly' ? 'active' : ''}`}
          onClick={() => setActiveTab('yearly')}
        >
          Yearly View
        </button>
      </div>

      {activeTab === 'entry' && renderEntry()}
      {activeTab === 'yearly' && renderYearly()}
    </div>
  );
}
